import React from "react";
import { motion } from "framer-motion";
import { PublicNav, Footer } from "@/components/PublicChrome";
import { Embers } from "@/components/Embers";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Mail } from "lucide-react";

const FAQS = [
  {
    q: "What is For Index Traders?",
    a: "For Index Traders is a proprietary trading firm built for Synthetic Index traders. We provide traders with simulated capital to showcase their skills and earn real payouts through performance-based challenges.",
  },
  {
    q: "How does For Index Traders work?",
    a: "Traders start by taking a challenge to prove their consistency and risk control. Once passed, we fund them with a simulated account that mirrors real trading conditions, allowing them to earn up to 80% of profits.",
  },
  {
    q: "Do you support Synthetic Indices?",
    a: "Yes. For Index Traders is created specifically for Deriv Synthetic Index traders, providing seamless access and fair trading rules for these unique markets.",
  },
  {
    q: "Is For Index Traders a broker?",
    a: "No. We are not a broker and do not accept deposits. F.I.T provides simulated trading environments and educational resources only.",
  },
  {
    q: "How do I get funded?",
    a: "Choose your preferred challenge plan, meet the profit target without breaching the rules, and you'll receive a funded account instantly after verification.",
  },
  {
    q: "How much profit can I keep?",
    a: "Traders can retain up to 80% of their profits. Payouts are processed promptly and securely through supported payment partners. Traders keep 50% of their profits on first payout, 60% on second payout, 70% on third payout, and 80% on fourth and subsequent payouts.",
  },
  {
    q: "What trading practices are restricted?",
    a: "F.I.T restricts any trading activity that manipulates market data, execution systems, or account performance. This includes latency arbitrage, trading without stop loss, copy or reverse trading, use of unfair Expert Advisors (EAs), martingale or grid strategies, and news spike exploitation. Traders must maintain ethical and transparent trading behavior; violations may result in disqualification or account termination.",
  },
 {
  q: "Are there any consistency rules?",
  a: (
    <>
      <p>
        Yes — F.I.T enforces both a Lot Size Consistency Rule and a 20%
        Profit Consistency Rule.
      </p>

      <p className="mt-4">
        Traders must maintain lot sizes between 50% and 200% of their first
        traded lot size. Trading outside this range constitutes a hard breach
        of the account.
      </p>

      <p className="mt-4">
        Additionally, at the time of a withdrawal request, no single profitable
        trading day may account for more than 20% of the account's total accumulated
        profit.
      </p>

      <p className="mt-4">
        Exceeding this threshold is not a hard breach; however, the account
        will not be eligible for payout until sufficient additional profits are
        generated to satisfy the 20% consistency requirement.
      </p>
    </>
  ),
},
  {
    q: "What does a profitable day mean?",
    a: "A profitable day is defined as a day where a profit of at least 0.5% of your account size is made. For instance: on a $5,000 account, if a $25 or more profit is made by the end of the day, that day is referred to as a profitable day — as $25 is 0.5% of $5,000.",
  },
  {
    q: "What are the Daily and Overall Drawdown rules?",
    a: "At F.I.T, traders are required to manage their risk within two core limits: the Daily Drawdown and the Overall Drawdown. Daily Drawdown — Set at 5% of your account balance at the start of each trading day (00:00 platform time). The daily drawdown includes both open and closed positions. Once your losses (realized or floating) reach 5% of that starting-day balance, your account is considered breached. Overall Drawdown — A static 10% limit based on your initial account balance. Unlike the daily drawdown, this limit does not move — it remains fixed regardless of profit growth. Breaching this limit will also result in the termination of your account. Example: If your funded account starts the day with a $10,000 balance, your maximum allowable loss for that day is $500 (5%). Your overall drawdown limit is $1,000, meaning your equity must never fall below $9,000.",
  },
  {
    q: "What can I trade?",
    a: "Traders are allowed to trade all and only Deriv Synthetic Indices.",
  },
  {
    q: "Can I hold trades overnight and over the weekend?",
    a: "Yes — For Index Traders fully allows holding trades overnight and over the weekend. Since Synthetic Indices operate 24/7, 365 days a year, traders are free to manage positions without any time-based restrictions. You are not required to close trades at the end of the trading day or before weekends. This flexibility allows you to maintain your strategies, including swing and long-term positions, without interruption. However, traders are advised to exercise proper risk management to account for potential market volatility during off-peak hours.",
  },
  {
    q: "What is the payout frequency?",
    a: "At F.I.T, traders are eligible to request payouts on a bi-weekly basis. This means you can withdraw your profits every two weeks once you meet the payout eligibility criteria. Our streamlined process ensures that payout requests are reviewed and processed promptly, allowing traders to enjoy timely access to their earnings while maintaining flexibility in their trading schedules.",
  },
  {
    q: "What is your trading platform?",
    a: "MT5 platform — Deriv MT5.",
  },
  {
    q: "How can I contact support?",
    a: "For assistance, contact help@forindextraders.com. Our global support team is available 24/7.",
  },
];

export default function FaqPage() {
  return (
    <div className="min-h-screen bg-[#050505] text-[#f5f5f5]">
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#ff7a00]/10 py-16 lg:py-20">
        <Embers count={16} />
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-[#ff7a00]/15 rounded-full blur-[140px]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-3" data-testid="faq-eyebrow">FAQ</div>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight"
          >
            Frequently asked <span className="text-[#ff7a00] text-glow-orange">questions.</span>
          </motion.h1>
          <p className="text-[#8a8a8a] mt-4 max-w-xl mx-auto">Everything you need to know about Funded Index — rules, payouts, platform, and more.</p>
        </div>
      </section>

      {/* FAQ list */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="glass rounded-xl p-2 sm:p-4 lg:p-6">
            <Accordion type="single" collapsible data-testid="faq-page-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-[#ff7a00]/10 last:border-b-0 px-2 sm:px-4"
                >
                  <AccordionTrigger
                    className="text-left text-[#f5f5f5] hover:text-[#ff9a2f] hover:no-underline font-display text-base sm:text-lg py-5"
                    data-testid={`faq-page-trigger-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="text-[#cfcfcf] text-sm sm:text-[15px] leading-relaxed pb-5"
                    data-testid={`faq-page-answer-${i}`}
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Still have questions */}
          <div className="mt-10 glass rounded-xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between" data-testid="faq-contact-card">
            <div>
              <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-1">Still need help?</div>
              <div className="font-display text-xl sm:text-2xl font-bold">Our team replies 24/7.</div>
              <p className="text-[#8a8a8a] text-sm mt-1">
                Email us at <a href="mailto:help@forindextraders.com" className="text-[#ff9a2f] hover:text-[#ff7a00]">help@forindextraders.com</a>
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="mailto:help@forindextraders.com" data-testid="faq-contact-email">
                <Button variant="outline" className="border-[#ff7a00]/40 bg-transparent text-[#f5f5f5] hover:bg-[#ff7a00]/10 hover:text-[#ff9a2f] hover:border-[#ff7a00]">
                  <Mail size={16} className="mr-2" /> Contact Support
                </Button>
              </a>
              <Link to="/challenges" data-testid="faq-cta-challenges">
                <Button className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold glow-orange-soft">
                  Start a Challenge <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
